import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from './ui/use-toast';
import { X } from 'lucide-react';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: 'lecture';
}

interface FormData {
  name: string;
  email: string;
  organization: string;
  phone: string;
  serviceType: 'lecture';
  targetAudience: string;
  description: string;
  timeline: string;
  honeypot: string; // Hidden field for bot detection
}

export default function ContactForm({ isOpen, onClose, serviceType }: ContactFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    phone: '',
    serviceType,
    targetAudience: '',
    description: '',
    timeline: '',
    honeypot: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Rate limiting check
  const checkRateLimit = (): boolean => {
    const lastSubmission = localStorage.getItem('lastContactSubmission');
    if (lastSubmission) {
      const timeDiff = Date.now() - parseInt(lastSubmission);
      const fiveMinutes = 5 * 60 * 1000;
      if (timeDiff < fiveMinutes) {
        const remainingTime = Math.ceil((fiveMinutes - timeDiff) / 60000);
        toast({
          title: t('contact.rateLimitTitle'),
          description: t('contact.rateLimitMessage', { minutes: remainingTime }),
          variant: 'destructive'
        });
        return false;
      }
    }
    return true;
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) newErrors.name = t('contact.errors.nameRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid');
    }
    // Organization is optional
    if (!formData.description.trim()) newErrors.description = t('contact.errors.descriptionRequired');
    if (formData.description.length > 1000) newErrors.description = t('contact.errors.descriptionTooLong');
    if (!formData.timeline) newErrors.timeline = t('contact.errors.timelineRequired');

    // Honeypot check - if filled, it's likely a bot
    if (formData.honeypot) {
      toast({
        title: t('contact.errors.botDetected'),
        description: t('contact.errors.botDetectedMessage'),
        variant: 'destructive'
      });
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkRateLimit() || !validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Set rate limit
      localStorage.setItem('lastContactSubmission', Date.now().toString());

      // Create email content
      const emailSubject = encodeURIComponent(
        `Foredrag forespørgsel fra ${formData.name}`
      );

      const emailBody = encodeURIComponent(`
Ny foredrag forespørgsel:

Navn: ${formData.name}
Email: ${formData.email}
Organisation: ${formData.organization || 'Ikke angivet'}
Telefon: ${formData.phone || 'Ikke angivet'}
Målgruppe: ${formData.targetAudience || 'Ikke angivet'}
Tidshorizont: ${formData.timeline}

Beskrivelse:
${formData.description}

---
Sendt fra omsorgsterapi.dk kontakt formular
      `);

      // Open email client with pre-filled content
      const mailtoLink = `mailto:info@omsorgsterapi.dk?subject=${emailSubject}&body=${emailBody}`;
      window.open(mailtoLink, '_blank');

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 500));

      toast({
        title: t('contact.success.title'),
        description: t('contact.success.message'),
        variant: 'default'
      });

      // Reset form and close
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        serviceType,
        targetAudience: '',
        description: '',
        timeline: '',
        honeypot: ''
      });
      onClose();

    } catch (error) {
      toast({
        title: t('contact.errors.submitFailed'),
        description: t('contact.errors.submitFailedMessage'),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-teal-700">
            {t('contact.lectureTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - hidden from users */}
          <input
            type="text"
            name="website"
            value={formData.honeypot}
            onChange={(e) => handleInputChange('honeypot', e.target.value)}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">{t('contact.fields.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                maxLength={100}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="email">{t('contact.fields.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
                maxLength={100}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="organization">{t('contact.fields.organization')}</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => handleInputChange('organization', e.target.value)}
                className={errors.organization ? 'border-red-500' : ''}
                maxLength={100}
              />
              {errors.organization && <p className="text-red-500 text-sm mt-1">{errors.organization}</p>}
            </div>

            <div>
              <Label htmlFor="phone">{t('contact.fields.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                maxLength={20}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="targetAudience">{t('contact.fields.targetAudience')}</Label>
            <Input
              id="targetAudience"
              value={formData.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e.target.value)}
              placeholder={t('contact.placeholders.targetAudience')}
              maxLength={200}
            />
          </div>

          <div>
            <Label htmlFor="timeline">{t('contact.fields.timeline')} *</Label>
            <Select value={formData.timeline} onValueChange={(value) => handleInputChange('timeline', value)}>
              <SelectTrigger className={errors.timeline ? 'border-red-500' : ''}>
                <SelectValue placeholder={t('contact.placeholders.timeline')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asap">{t('contact.timeline.asap')}</SelectItem>
                <SelectItem value="1-3months">{t('contact.timeline.1-3months')}</SelectItem>
                <SelectItem value="3-6months">{t('contact.timeline.3-6months')}</SelectItem>
                <SelectItem value="6months+">{t('contact.timeline.6months+')}</SelectItem>
                <SelectItem value="flexible">{t('contact.timeline.flexible')}</SelectItem>
              </SelectContent>
            </Select>
            {errors.timeline && <p className="text-red-500 text-sm mt-1">{errors.timeline}</p>}
          </div>

          <div>
            <Label htmlFor="description">{t('contact.fields.description')} *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('contact.placeholders.description')}
              className={errors.description ? 'border-red-500' : ''}
              rows={4}
              maxLength={1000}
            />
            <p className="text-sm text-gray-500 mt-1">
              {formData.description.length}/1000 {t('contact.charactersRemaining')}
            </p>
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            {t('contact.notice')}
          </p>

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-600 hover:bg-teal-700">
              {isSubmitting ? t('contact.submitting') : t('contact.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


